import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { getClassNameFactory } from "../../../../utilities";
import Button, { ButtonColor, ButtonKind } from "../../../../elements/Button";
import OpenInNewTab2 from "@dcl/icons/esm/ui/open-in-new-tab-2";
var colWidthConfig = {
  1: {
    titleColumnSize: 6,
    contentColumnSize: 6,
    oneColumnSizePercent: 8.3,
    totalSize: 12
  },
  2: {
    titleColumnSize: 4,
    contentColumnSize: 4,
    oneColumnSizePercent: 8.3,
    totalSize: 12
  },
  3: {
    titleColumnSize: 4,
    contentColumnSize: 2.6,
    oneColumnSizePercent: 8.3,
    totalSize: 12
  },
  4: {
    titleColumnSize: 4,
    contentColumnSize: 2,
    oneColumnSizePercent: 8.3,
    totalSize: 12
  }
};
var desktopWidth = 768;

var PlanInfo = function PlanInfo(_ref) {
  var _buttonRef$current3;

  var planName = _ref.planName,
      price = _ref.price,
      priceInfo = _ref.priceInfo,
      description = _ref.description,
      actionLabel = _ref.actionLabel,
      ariaLabel = _ref.ariaLabel,
      target = _ref.target,
      actionButtonProps = _ref.actionButtonProps;
  var getClassName = getClassNameFactory(PlanInfo.displayName);

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      buttonHeight = _useState2[0],
      setButtonHeight = _useState2[1];

  var buttonRef = useRef();
  useLayoutEffect(function () {
    var _buttonRef$current, _buttonRef$current$ge;

    if (!(buttonRef !== null && buttonRef !== void 0 && buttonRef.current)) {
      return;
    }

    if ((buttonRef === null || buttonRef === void 0 ? void 0 : (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : (_buttonRef$current$ge = _buttonRef$current.getBoundingClientRect()) === null || _buttonRef$current$ge === void 0 ? void 0 : _buttonRef$current$ge.height) !== 0) {
      var _buttonRef$current2;

      var tableNode = buttonRef.current.closest(".emcmUi-PlanComparisonTable"); // Calculate the maximum height among elements

      var planNames = tableNode.querySelectorAll(".emcmUi-PlanComparisonTablePlanInfo-planName");
      var featureCells = tableNode.querySelectorAll(".emcmUi-PlanComparisonTableFeatureCell:not(.emcmUi-PlanComparisonTableFeatureCell-featureName)");

      if (window.innerWidth >= desktopWidth) {
        var thCols = Array.from(tableNode.querySelectorAll("thead tr:first-child th"));
        var columnsCount = thCols.length;
        var titleColumn = thCols.shift();
        var sizeOfColumns = colWidthConfig[columnsCount - 1];
        titleColumn.style.width = "".concat(Math.round((sizeOfColumns === null || sizeOfColumns === void 0 ? void 0 : sizeOfColumns.titleColumnSize) * (sizeOfColumns === null || sizeOfColumns === void 0 ? void 0 : sizeOfColumns.oneColumnSizePercent)), "%");
        thCols.forEach(function (column) {
          var colWidth = "".concat(Math.round((sizeOfColumns === null || sizeOfColumns === void 0 ? void 0 : sizeOfColumns.contentColumnSize) * (sizeOfColumns === null || sizeOfColumns === void 0 ? void 0 : sizeOfColumns.oneColumnSizePercent)), "%");
          column.style.width = colWidth;
        });
      } // Set the minimum height for all feature cell buttons to take 100% height of the table cell


      featureCells.forEach(function (cell) {
        cell.childNodes[0].style.removeProperty("min-height");
        var tableCellHeight = cell.parentNode.parentNode.clientHeight;
        cell.childNodes[0].style.minHeight = "".concat(tableCellHeight, "px");
      });
      var max = 0;
      planNames.forEach(function (element) {
        max = Math.max(max, element.clientHeight);
      }); // Set the maximum height for all elements

      planNames.forEach(function (element) {
        element.style.height = "".concat(max, "px");
      });
      setButtonHeight(buttonRef === null || buttonRef === void 0 ? void 0 : (_buttonRef$current2 = buttonRef.current) === null || _buttonRef$current2 === void 0 ? void 0 : _buttonRef$current2.clientHeight);
    }
  }, [buttonRef === null || buttonRef === void 0 ? void 0 : (_buttonRef$current3 = buttonRef.current) === null || _buttonRef$current3 === void 0 ? void 0 : _buttonRef$current3.clientHeight]); // Run this effect once when the component mounts

  return /*#__PURE__*/React.createElement("div", {
    className: getClassName(),
    style: {
      marginBottom: buttonHeight
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: getClassName({
      descendantName: "plan-name"
    })
  }, planName), /*#__PURE__*/React.createElement("div", {
    className: getClassName({
      descendantName: "price"
    })
  }, price, priceInfo && /*#__PURE__*/React.createElement("p", {
    className: getClassName({
      descendantName: "info"
    })
  }, priceInfo)), description && /*#__PURE__*/React.createElement("p", {
    className: getClassName({
      descendantName: "text"
    })
  }, description), actionLabel && /*#__PURE__*/React.createElement("div", {
    className: getClassName({
      descendantName: "btn"
    }),
    ref: buttonRef
  }, /*#__PURE__*/React.createElement(Button, _extends({
    block: true,
    color: ButtonColor.SECONDARY,
    kind: ButtonKind.LINK,
    newWindow: target === "_blank",
    ariaLabel: ariaLabel
  }, actionButtonProps), /*#__PURE__*/React.createElement("span", null, actionLabel, target && target === "_blank" && /*#__PURE__*/React.createElement(OpenInNewTab2, {
    size: 16
  })))));
};

PlanInfo.displayName = "PlanComparisonTable.PlanInfo";
PlanInfo.propTypes = {
  /**
   * Plan name
   */
  planName: PropTypes.string.isRequired,

  /**
   * Price
   */
  price: PropTypes.string.isRequired,

  /**
   * Price info, per length
   */
  priceInfo: PropTypes.string,

  /**
   * Description
   */
  description: PropTypes.string,

  /**
   * Button label
   */
  actionLabel: PropTypes.string,

  /**
   * Button aria label
   */
  ariaLabel: PropTypes.string,

  /**
   * Target for the button, _blank or _self
   */
  target: PropTypes.string,

  /**
   * Props for action button, color, href, etc
   */
  actionButtonProps: PropTypes.object
};
PlanInfo.defaultProps = {
  actionButtonProps: {
    href: "#"
  },
  target: "_self"
};
export default PlanInfo;